import { useEffect, useState } from 'react';
import { create } from 'zustand';

import { FALLOFFS, MATCAPS, statsLines, type MeshMode, type MeshOptions, type SnapElement } from '../../state/mesh-edit.js';
import { useModel } from '../../state/model.js';
import { ContextMenu, MenuButton, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { ADJUST_FIELDS, menuItems, opTitle, shortcutOf, type MeshApi, type MeshMenuId } from './mesh-keymap.js';
import './mesh-edit.css';

/** Popup menu opened by a key (M, X, Shift+G...) at the mouse. */
const useMeshMenu = create<{ menu: { id: MeshMenuId; x: number; y: number } | null }>(() => ({ menu: null }));

let pointer = { x: 200, y: 200 };
if (typeof window !== 'undefined') {
  window.addEventListener('pointermove', (e) => (pointer = { x: e.clientX, y: e.clientY }), { passive: true });
}

/** The store as the keymap's command API. */
export function meshApi(): MeshApi {
  const s = useModel.getState();
  return {
    op: (name, params) => s.meshOp(name, params),
    tool: (tool) => s.meshTool(tool),
    mode: (mode) => s.setMeshMode(mode),
    options: (patch) => s.setMeshOptions(patch),
    getOptions: () => useModel.getState().meshOptions,
    menu: (id) => useMeshMenu.setState({ menu: { id, x: pointer.x, y: pointer.y } }),
    editMode: (on) => s.setMeshEdit(on),
    toggleEditMode: () => s.setMeshEdit(!useModel.getState().meshEdit.active),
    frame: () => s.frameSelected(),
  };
}

const tip = (label: string, id: string) => {
  const k = shortcutOf(`model.mesh.${id}`);
  return k ? `${label} (${k})` : label;
};

const check = (on: boolean, label: string) => `${on ? '✓ ' : '   '}${label}`;

function menuOf(id: MeshMenuId): MenuItem[] {
  return menuItems(id, meshApi());
}

/** Overlay / shading menu items (object mode too). */
export function overlayItems(o: MeshOptions): MenuItem[] {
  const set = (patch: Partial<MeshOptions>) => () => useModel.getState().setMeshOptions(patch);
  return [
    { id: 'h1', label: 'Overlays', heading: true },
    { id: 'stats', label: check(o.stats, 'Statistics'), run: set({ stats: !o.stats }) },
    { id: 'fn', label: check(o.faceNormals, 'Face Normals'), run: set({ faceNormals: !o.faceNormals }) },
    { id: 'vn', label: check(o.vertexNormals, 'Vertex Normals'), run: set({ vertexNormals: !o.vertexNormals }) },
    { id: 'nl', label: `   Normal Size: ${o.normalLength.toFixed(2)} (click to double)`, run: set({ normalLength: o.normalLength >= 1.6 ? 0.05 : o.normalLength * 2 }) },
    { id: 'fo', label: check(o.faceOrientation, 'Face Orientation'), run: set({ faceOrientation: !o.faceOrientation }) },
    { id: 'h2', label: 'Measurement (edit mode)', heading: true },
    { id: 'el', label: check(o.edgeLength, 'Edge Length'), run: set({ edgeLength: !o.edgeLength }) },
    { id: 'ea', label: check(o.edgeAngle, 'Edge Angle'), run: set({ edgeAngle: !o.edgeAngle }) },
    { id: 'fa', label: check(o.faceArea, 'Face Area'), run: set({ faceArea: !o.faceArea }) },
    { id: 'h3', label: 'Viewport', heading: true },
    { id: 'xr', label: check(o.xray, 'X-Ray (see-through select)'), shortcut: 'Alt+Z', run: set({ xray: !o.xray }) },
    { id: 'qv', label: check(o.quad, 'Quad View'), shortcut: 'Ctrl+Alt+Q', run: set({ quad: !o.quad }) },
  ];
}

export function shadingItems(o: MeshOptions): MenuItem[] {
  return [
    { id: 'h', label: 'Matcap', heading: true },
    ...MATCAPS.map((m) => ({ id: `m-${m.id || 'none'}`, label: check(o.matcap === m.id, m.label), run: () => useModel.getState().setMeshOptions({ matcap: m.id }) })),
  ];
}

function snapItems(o: MeshOptions): MenuItem[] {
  const set = (patch: Partial<MeshOptions>) => () => useModel.getState().setMeshOptions(patch);
  const toggle = (el: SnapElement) => set({ snapElements: o.snapElements.includes(el) ? o.snapElements.filter((x) => x !== el) : [...o.snapElements, el] });
  const elements: Array<[SnapElement, string]> = [
    ['increment', 'Increment'],
    ['vertex', 'Vertex'],
    ['edge', 'Edge'],
    ['edgeMidpoint', 'Edge Center'],
    ['face', 'Face (project)'],
  ];
  return [
    { id: 'on', label: check(o.snap, 'Snapping on (Ctrl inverts while moving)'), shortcut: 'Shift+Tab', run: set({ snap: !o.snap }) },
    { id: 'h1', label: 'Snap To', heading: true },
    ...elements.map(([el, label]) => ({ id: el, label: check(o.snapElements.includes(el), label), run: toggle(el) })),
    { id: 'inc', label: `   Increment: ${Number(o.increment.toPrecision(4))} (click to cycle)`, run: set({ increment: o.increment >= 1 ? 0.01 : Number((o.increment * 10).toFixed(4)) }) },
    { id: 'h2', label: 'Snap With', heading: true },
    ...(['closest', 'center', 'median', 'active'] as const).map((b) => ({ id: `b-${b}`, label: check(o.snapBase === b, b.charAt(0).toUpperCase() + b.slice(1)), run: set({ snapBase: b }) })),
    { id: 'align', label: check(o.alignRotation, 'Align Rotation to Target'), run: set({ alignRotation: !o.alignRotation }) },
  ];
}

function proportionalItems(o: MeshOptions): MenuItem[] {
  const set = (patch: Partial<MeshOptions>) => () => useModel.getState().setMeshOptions(patch);
  return [
    { id: 'on', label: check(o.proportional, 'Proportional Editing'), shortcut: 'O', run: set({ proportional: !o.proportional }) },
    { id: 'conn', label: check(o.connected, 'Connected Only'), shortcut: 'Alt+O', run: set({ connected: !o.connected }) },
    { id: 'r', label: `   Radius: ${o.radius.toFixed(3)} (wheel while moving)`, disabled: true },
    { id: 'h', label: 'Falloff', heading: true },
    ...FALLOFFS.map((f) => ({ id: f.id, label: check(o.falloff === f.id, f.label), run: set({ falloff: f.id, proportional: true }) })),
  ];
}

/** Second toolbar row while editing a mesh: modes, menus, tools and options. */
export function MeshToolbar() {
  const mesh = useModel((s) => s.meshEdit);
  const o = useModel((s) => s.meshOptions);
  const s = useModel.getState();
  const api = meshApi();
  const modeButton = (mode: MeshMode, label: string, key: string) => (
    <button
      type="button"
      className={`tb-btn${mesh.mode === mode ? ' active' : ''}`}
      title={`${label} select (${key})`}
      aria-pressed={mesh.mode === mode}
      onClick={() => s.setMeshMode(mode)}
    >
      {label}
    </button>
  );
  const toolButton = (id: string, label: string, run: () => void, disabled = false) => (
    <button type="button" className="tb-btn" title={tip(label, id)} disabled={disabled} onClick={run}>
      {label}
    </button>
  );
  const busy = mesh.modal !== null;
  return (
    <div className="viewport-toolbar mesh-toolbar" role="toolbar" aria-label="Mesh edit">
      <div className="tb-group segmented">
        {modeButton('vertex', 'Vertex', '1')}
        {modeButton('edge', 'Edge', '2')}
        {modeButton('face', 'Face', '3')}
      </div>
      <div className="tb-group segmented">
        {(['select', 'mesh', 'vertex', 'edge', 'face'] as const).map((id) => (
          <MenuButton key={id} className="tb-btn" items={() => menuOf(id)} title={`${id.charAt(0).toUpperCase()}${id.slice(1)} menu`}>
            {id.charAt(0).toUpperCase() + id.slice(1)} <Icon name="chevronDown" size={11} />
          </MenuButton>
        ))}
      </div>
      <div className="tb-group segmented">
        {toolButton('extrude', 'Extrude', () => api.op('extrude'), busy || mesh.mode !== 'face')}
        {toolButton('inset', 'Inset', () => api.op('inset'), busy || mesh.mode !== 'face')}
        {toolButton('loopCut', 'Loop Cut', () => api.tool('loopCut'), busy)}
        {toolButton('knife', 'Knife', () => api.tool('knife'), busy)}
        {toolButton('bevel', 'Bevel', () => api.tool(mesh.mode === 'vertex' ? 'bevelVertices' : 'bevel'), busy)}
        {toolButton('bisect', 'Bisect', () => api.tool('bisect'), busy)}
        {toolButton('bridge', 'Bridge', () => api.op('bridge'), busy)}
      </div>
      <div className="tb-group segmented">
        <MenuButton className={`tb-btn${o.proportional ? ' active' : ''}`} items={() => proportionalItems(useModel.getState().meshOptions)} title="Proportional editing (O)">
          <Icon name="circle" size={12} /> {o.proportional ? FALLOFFS.find((f) => f.id === o.falloff)?.label : 'Proportional'}
        </MenuButton>
        <MenuButton className={`tb-btn${o.snap ? ' active' : ''}`} items={() => snapItems(useModel.getState().meshOptions)} title="Snapping while moving (Shift+Tab; hold Ctrl to invert)">
          <Icon name="magnet" size={12} /> Snap
        </MenuButton>
      </div>
      <div className="tb-group segmented" title="Live mirror editing across the part's axes">
        <span className="mesh-label">Mirror</span>
        {(['X', 'Y', 'Z'] as const).map((axis, i) => (
          <button
            key={axis}
            type="button"
            className={`tb-btn${o.mirror[i] ? ' active' : ''}`}
            aria-pressed={o.mirror[i]}
            title={`Mirror edits across ${axis}`}
            onClick={() => {
              const mirror = [...o.mirror] as [boolean, boolean, boolean];
              mirror[i] = !mirror[i];
              s.setMeshOptions({ mirror });
            }}
          >
            {axis}
          </button>
        ))}
        <button
          type="button"
          className={`tb-btn${o.topologyMirror ? ' active' : ''}`}
          aria-pressed={o.topologyMirror}
          title="Topology mirror: pair vertices through the mesh when positions are not exactly symmetric"
          onClick={() => s.setMeshOptions({ topologyMirror: !o.topologyMirror })}
        >
          Topo
        </button>
      </div>
      <span className="muted mesh-count">
        {mesh.selected} selected{mesh.status ? ` · ${mesh.status}` : ''}
      </span>
    </div>
  );
}

/** Viewport overlays: statistics, tool hints, measurement labels, quad view captions, popup menus and the Adjust Last Operation panel. */
export function MeshViewportOverlay() {
  const mesh = useModel((s) => s.meshEdit);
  const o = useModel((s) => s.meshOptions);
  const labels = useModel((s) => s.meshLabels);
  const stats = useModel((s) => s.meshEdit.stats);
  const menu = useMeshMenu((m) => m.menu);
  return (
    <>
      {o.quad && (
        <div className="mesh-quad" aria-hidden>
          <span style={{ left: 8, top: 30 }}>Top</span>
          <span style={{ left: 'calc(50% + 8px)', top: 30 }}>Perspective</span>
          <span style={{ left: 8, top: 'calc(50% + 6px)' }}>Front</span>
          <span style={{ left: 'calc(50% + 8px)', top: 'calc(50% + 6px)' }}>Right</span>
          <i className="mesh-quad-h" />
          <i className="mesh-quad-v" />
        </div>
      )}
      {mesh.active && o.stats && stats && (
        <div className={`mesh-stats${o.quad ? ' is-quad' : ''}`} aria-label="Mesh statistics">
          {statsLines(stats, mesh.mode).map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
      )}
      {mesh.active &&
        labels.map((l, i) => (
          <span key={i} className="mesh-measure" style={{ left: l.x, top: l.y }}>
            {l.text}
          </span>
        ))}
      {mesh.modal && <ModalHint />}
      {mesh.active && !mesh.modal && mesh.last && ADJUST_FIELDS[mesh.last.name] && <AdjustLastPanel />}
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menuOf(menu.id)} onClose={() => useMeshMenu.setState({ menu: null })} />}
    </>
  );
}

function ModalHint() {
  const modal = useModel((s) => s.meshEdit.modal);
  const s = useModel.getState();
  if (!modal) return null;
  const facts: string[] = [];
  for (const [k, v] of Object.entries(modal)) {
    if (k === 'tool' || k === 'hint' || v === null || v === undefined) continue;
    facts.push(`${opTitle(k)}: ${typeof v === 'number' ? Number(v.toFixed(3)) : String(v)}`);
  }
  return (
    <div className="mesh-hint" role="status">
      <strong>{opTitle(modal.tool)}</strong>
      <span>{facts.join(' · ')}</span>
      <span className="muted">{modal.hint}</span>
      <button type="button" className="tb-btn" onClick={() => s.meshOp('confirmTool')}>
        Confirm
      </button>
      <button type="button" className="tb-btn" onClick={() => s.meshOp('cancelTool')}>
        Cancel
      </button>
    </div>
  );
}

/** Blender's "Adjust Last Operation" (F9): edits re-run the operation as one undo step. */
function AdjustLastPanel() {
  const last = useModel((s) => s.meshEdit.last);
  const [open, setOpen] = useState(true);
  useEffect(() => setOpen(true), [last?.name]);
  if (!last) return null;
  const fields = ADJUST_FIELDS[last.name] ?? [];
  const s = useModel.getState();
  return (
    <div className="mesh-adjust" role="group" aria-label="Adjust last operation">
      <button type="button" className="mesh-adjust-title" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={11} /> {opTitle(last.name)}
      </button>
      {open &&
        fields.map((f) => {
          const value = last[f.key];
          const id = `mesh-adjust-${f.key}`;
          return (
            <label key={f.key} className="mesh-adjust-row" htmlFor={id}>
              <span>{f.label}</span>
              {f.kind === 'bool' ? (
                <input id={id} type="checkbox" checked={Boolean(value)} onChange={(e) => s.meshRedoLast({ [f.key]: e.target.checked })} />
              ) : f.kind === 'axis' ? (
                <select id={id} value={Number(value ?? 0)} onChange={(e) => s.meshRedoLast({ [f.key]: Number(e.target.value) })}>
                  <option value={0}>X</option>
                  <option value={1}>Y</option>
                  <option value={2}>Z</option>
                </select>
              ) : (
                <input
                  id={id}
                  type="number"
                  value={typeof value === 'number' ? Number(value.toFixed(5)) : ''}
                  placeholder="auto"
                  min={f.min}
                  max={f.max}
                  step={f.kind === 'int' ? 1 : (f.step ?? 0.01)}
                  onChange={(e) => {
                    if (e.target.value === '') return;
                    const n = f.kind === 'int' ? Math.round(Number(e.target.value)) : Number(e.target.value);
                    if (Number.isFinite(n)) s.meshRedoLast({ [f.key]: n });
                  }}
                />
              )}
            </label>
          );
        })}
    </div>
  );
}
